import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Form, Input, Modal } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUserUpdatePassword } from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes } from "../../../../common/constants";
import { AgentUserProfile, ClientUserProfile, UserUpdatePassword } from "../../types";

interface Props {
  open: boolean;
  user: AgentUserProfile | ClientUserProfile;
  onFormCancel: () => void;
}

export const UpdatePasswordForm = ({ open, user, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<UserUpdatePassword>();

  const updatePassword = useUserUpdatePassword();

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        optimisticLockVersion: user.optimisticLockVersion,
        previousPassword: undefined,
        newPassword: undefined,
        newPasswordRepeat: undefined,
      });
    }
  }, [open, user, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        updatePassword.mutate(
          { id: user.id, object: values },
          {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "user.attrs", error.violations),
          },
        );
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={open}
      title={t("user.titles.updatePassword")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={updatePassword.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="updatePasswordForm">
        <HiddenInput name="optimisticLockVersion" />

        <Form.Item name="previousPassword" label={t("user.attrs.previousPassword")} rules={[validations.notBlank]}>
          <Input.Password />
        </Form.Item>

        <Form.Item name="newPassword" label={t("user.attrs.newPassword")} rules={validations.sufficientPassword}>
          <Input.Password />
        </Form.Item>

        <Form.Item
          name="newPasswordRepeat"
          label={t("user.attrs.newPasswordRepeat")}
          rules={[...validations.sufficientPassword, validations.repeatedPassword("newPassword")]}
        >
          <Input.Password />
        </Form.Item>
      </Form>
    </Modal>
  );
};
